import {
  CAMERA_TOOLTIP_THEME_TOKEN_KEYS,
  CAMERA_TOOLTIP_TYPES,
  CAMERA_INTERACTION_ACTIONS,
  MAP_STATUS_CODES,
  MAP_STATUS_THEME_TOKEN_KEYS,
  MAP_STREET_THEME_GROUPS,
  MAP_STREET_THEME_TOKEN_KEYS,
  TOOLTIP_DEBT_STATES,
  TOOLTIP_THEME_TOKEN_KEYS,
} from './constants';
import {
  CameraInteractionAction,
  type CameraTooltipType,
  type MapApiResult,
  type MapStatusCode,
  type TotalDebtApiResult,
} from './types';

/**
 * Performs one API request and validates the standard `result + error` envelope.
 *
 * @param url Backend endpoint URL.
 * @param defaultErrorMessage Fallback error text for broken responses.
 * @returns Parsed `result` payload from the API.
 * @throws {Error} When the request, response envelope or backend error state is invalid.
 */
async function fetchApiResult(url: string, defaultErrorMessage: string): Promise<unknown> {
  const response = await fetch(url, {
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(defaultErrorMessage);
  }

  const payload = (await response.json()) as {
    result?: unknown;
    error?: { message?: string } | null;
  };

  if (payload.error !== null && payload.error !== undefined) {
    throw new Error(payload.error.message ?? defaultErrorMessage);
  }

  if (payload.result === null || payload.result === undefined) {
    throw new Error(defaultErrorMessage);
  }

  return payload.result;
}

/**
 * Checks whether a value is a plain object suitable for record-style validation.
 *
 * @param value Unknown runtime value from API payload.
 * @returns `true` when the value can be treated as record-like object.
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Validates camera identity fields from one `/api/v1/map` camera item.
 *
 * @param cameraCandidate Record-like camera payload from API.
 * @param index Camera array index used in error messages.
 * @returns Nothing.
 * @throws {TypeError} When required camera identity fields are malformed.
 */
function validateCameraIdentityPayload(
  cameraCandidate: Record<string, unknown>,
  index: number,
): void {
  if (!Number.isInteger(cameraCandidate.mapPositionId)) {
    throw new TypeError(`В payload карты cameras[${index}].mapPositionId должен быть integer.`);
  }

  if (!Number.isInteger(cameraCandidate.cameraId)) {
    throw new TypeError(`В payload карты cameras[${index}].cameraId должен быть integer.`);
  }

  if (typeof cameraCandidate.title !== 'string' || cameraCandidate.title.trim() === '') {
    throw new TypeError(`В payload карты cameras[${index}].title должен быть непустой строкой.`);
  }
}

/**
 * Validates SVG placement geometry from one `/api/v1/map` camera item.
 *
 * @param cameraCandidate Record-like camera payload from API.
 * @param index Camera array index used in error messages.
 * @returns Nothing.
 * @throws {TypeError} When camera SVG placement values are malformed.
 */
function validateCameraGeometryPayload(
  cameraCandidate: Record<string, unknown>,
  index: number,
): void {
  if (!Number.isInteger(cameraCandidate.positionX)) {
    throw new TypeError(`В payload карты cameras[${index}].positionX должен быть integer.`);
  }

  if (!Number.isInteger(cameraCandidate.positionY)) {
    throw new TypeError(`В payload карты cameras[${index}].positionY должен быть integer.`);
  }

  if (!Number.isInteger(cameraCandidate.rotationDegrees)) {
    throw new TypeError(`В payload карты cameras[${index}].rotationDegrees должен быть integer.`);
  }
}

/**
 * Validates SVG icon settings from one `/api/v1/map` camera item.
 *
 * @param iconCandidate Unknown icon payload from API.
 * @param index Camera array index used in error messages.
 * @returns Nothing.
 * @throws {TypeError} When icon color or scale data is malformed.
 */
function validateCameraIconPayload(iconCandidate: unknown, index: number): void {
  if (!isRecord(iconCandidate)) {
    throw new TypeError(
      `В payload карты cameras[${index}].icon отсутствует или имеет некорректный формат.`,
    );
  }

  if (typeof iconCandidate.color !== 'string' || iconCandidate.color.trim() === '') {
    throw new TypeError(
      `В payload карты cameras[${index}].icon.color должен быть непустой строкой.`,
    );
  }

  if (
    typeof iconCandidate.sizeMultiplier !== 'number' ||
    !Number.isFinite(iconCandidate.sizeMultiplier)
  ) {
    throw new TypeError(
      `В payload карты cameras[${index}].icon.sizeMultiplier должен быть числом.`,
    );
  }
}

/**
 * Validates camera label settings from one `/api/v1/map` camera item.
 *
 * @param labelCandidate Unknown label payload from API.
 * @param index Camera array index used in error messages.
 * @returns Nothing.
 * @throws {TypeError} When label content, size or offsets are malformed.
 */
function validateCameraLabelPayload(labelCandidate: unknown, index: number): void {
  if (!isRecord(labelCandidate)) {
    throw new TypeError(
      `В payload карты cameras[${index}].label отсутствует или имеет некорректный формат.`,
    );
  }

  if (typeof labelCandidate.text !== 'string') {
    throw new TypeError(`В payload карты cameras[${index}].label.text должен быть строкой.`);
  }

  if (typeof labelCandidate.color !== 'string' || labelCandidate.color.trim() === '') {
    throw new TypeError(
      `В payload карты cameras[${index}].label.color должен быть непустой строкой.`,
    );
  }

  if (!Number.isInteger(labelCandidate.fontSize)) {
    throw new TypeError(`В payload карты cameras[${index}].label.fontSize должен быть integer.`);
  }

  if (!Number.isInteger(labelCandidate.offsetX)) {
    throw new TypeError(`В payload карты cameras[${index}].label.offsetX должен быть integer.`);
  }

  if (!Number.isInteger(labelCandidate.offsetY)) {
    throw new TypeError(`В payload карты cameras[${index}].label.offsetY должен быть integer.`);
  }
}

/**
 * Validates display-ready camera popup payload from one `/api/v1/map` camera item.
 *
 * @param tooltipCandidate Unknown tooltip payload from API.
 * @param index Camera array index used in error messages.
 * @returns Nothing.
 * @throws {TypeError} When camera popup copy or type is malformed.
 */
function validateCameraTooltipPayload(tooltipCandidate: unknown, index: number): void {
  if (!isRecord(tooltipCandidate)) {
    throw new TypeError(
      `В payload карты cameras[${index}].tooltip отсутствует или имеет некорректный формат.`,
    );
  }

  if (
    typeof tooltipCandidate.type !== 'string' ||
    !CAMERA_TOOLTIP_TYPES.includes(tooltipCandidate.type as CameraTooltipType)
  ) {
    throw new TypeError(
      `В payload карты cameras[${index}].tooltip.type имеет неизвестное значение.`,
    );
  }

  if (typeof tooltipCandidate.title !== 'string' || tooltipCandidate.title.trim() === '') {
    throw new TypeError(
      `В payload карты cameras[${index}].tooltip.title должен быть непустой строкой.`,
    );
  }

  if (
    typeof tooltipCandidate.description !== 'string' ||
    tooltipCandidate.description.trim() === ''
  ) {
    throw new TypeError(
      `В payload карты cameras[${index}].tooltip.description должен быть непустой строкой.`,
    );
  }

  if (
    typeof tooltipCandidate.actionText !== 'string' ||
    tooltipCandidate.actionText.trim() === ''
  ) {
    throw new TypeError(
      `В payload карты cameras[${index}].tooltip.actionText должен быть непустой строкой.`,
    );
  }
}

/**
 * Validates display-ready camera interaction state from one `/api/v1/map` camera item.
 *
 * @param interactionCandidate Unknown interaction payload from API.
 * @param index Camera array index used in error messages.
 * @returns Nothing.
 * @throws {TypeError} When camera access action and URL combinations are inconsistent.
 */
function validateCameraInteractionPayload(interactionCandidate: unknown, index: number): void {
  if (!isRecord(interactionCandidate)) {
    throw new TypeError(
      `В payload карты cameras[${index}].interaction отсутствует или имеет некорректный формат.`,
    );
  }

  if (typeof interactionCandidate.isClickable !== 'boolean') {
    throw new TypeError(
      `В payload карты cameras[${index}].interaction.isClickable должен быть boolean.`,
    );
  }

  if (
    typeof interactionCandidate.action !== 'string' ||
    !CAMERA_INTERACTION_ACTIONS.includes(interactionCandidate.action as CameraInteractionAction)
  ) {
    throw new TypeError(
      `В payload карты cameras[${index}].interaction.action имеет неизвестное значение.`,
    );
  }

  if (!interactionCandidate.isClickable) {
    if (interactionCandidate.action !== CameraInteractionAction.None) {
      throw new TypeError(`В payload карты cameras[${index}].interaction.action должен быть none.`);
    }
    if (interactionCandidate.targetUrl !== null) {
      throw new TypeError(
        `В payload карты cameras[${index}].interaction.targetUrl должен быть null.`,
      );
    }
    return;
  }

  if (interactionCandidate.action === CameraInteractionAction.OpenCameras) {
    if (
      typeof interactionCandidate.targetUrl !== 'string' ||
      interactionCandidate.targetUrl.trim() === ''
    ) {
      throw new TypeError(
        `В payload карты cameras[${index}].interaction.targetUrl должен быть непустой строкой.`,
      );
    }
    return;
  }

  if (interactionCandidate.targetUrl !== null) {
    throw new TypeError(
      `В payload карты cameras[${index}].interaction.targetUrl должен быть null.`,
    );
  }
}

/**
 * Validates one camera payload from `/api/v1/map`.
 *
 * @param cameraCandidate Unknown runtime camera value from the API.
 * @param index Camera array index used in error messages.
 * @returns Nothing.
 * @throws {TypeError} When any required camera sub-contract is malformed.
 */
function validateCameraPayload(cameraCandidate: unknown, index: number): void {
  if (!isRecord(cameraCandidate)) {
    throw new TypeError(`Элемент cameras[${index}] в payload карты имеет некорректный формат.`);
  }

  validateCameraIdentityPayload(cameraCandidate, index);
  validateCameraGeometryPayload(cameraCandidate, index);
  validateCameraIconPayload(cameraCandidate.icon, index);
  validateCameraLabelPayload(cameraCandidate.label, index);
  validateCameraTooltipPayload(cameraCandidate.tooltip, index);
  validateCameraInteractionPayload(cameraCandidate.interaction, index);
}

/**
 * Validates one legend row from `/api/v1/map`.
 *
 * @param legendCandidate Unknown runtime legend item from the API.
 * @param index Legend array index used in error messages.
 * @returns Nothing.
 * @throws {TypeError} When legend status, count or description is malformed.
 */
function validateLegendPayload(legendCandidate: unknown, index: number): void {
  if (!isRecord(legendCandidate)) {
    throw new TypeError(`Элемент legend[${index}] в payload карты имеет некорректный формат.`);
  }

  if (
    typeof legendCandidate.status !== 'string' ||
    !MAP_STATUS_CODES.includes(legendCandidate.status as MapStatusCode)
  ) {
    throw new TypeError(`В payload карты legend[${index}].status имеет неизвестное значение.`);
  }

  if (!Number.isInteger(legendCandidate.count)) {
    throw new TypeError(`В payload карты legend[${index}].count должен быть integer.`);
  }

  if (
    typeof legendCandidate.description !== 'string' ||
    legendCandidate.description.trim() === ''
  ) {
    throw new TypeError(
      `В payload карты legend[${index}].description должен быть непустой строкой.`,
    );
  }
}

/**
 * Validates one theme record group-by-group against the agreed API contract.
 *
 * @param params Theme payload and validation metadata.
 * @returns Nothing.
 * @throws {TypeError} When required theme groups or visual tokens are missing.
 */
function validateThemeRecord(params: {
  themeRecord: unknown;
  requiredGroups: readonly string[];
  requiredTokenKeys: readonly string[];
  themeName: string;
}): void {
  const { themeRecord, requiredGroups, requiredTokenKeys, themeName } = params;

  if (!isRecord(themeRecord)) {
    throw new TypeError(`Некорректная тема "${themeName}" в payload карты.`);
  }

  for (const groupKey of requiredGroups) {
    const themeGroup = themeRecord[groupKey];

    if (!isRecord(themeGroup)) {
      throw new TypeError(
        `В payload карты отсутствует группа "${groupKey}" для темы "${themeName}".`,
      );
    }

    for (const tokenKey of requiredTokenKeys) {
      const tokenValue = themeGroup[tokenKey];

      if (typeof tokenValue !== 'string' || tokenValue.trim() === '') {
        throw new TypeError(
          `В payload карты отсутствует токен "${groupKey}.${tokenKey}" для темы "${themeName}".`,
        );
      }
    }
  }
}

/**
 * Validates that all required theme blocks are present in the map API payload.
 *
 * @param result Raw `/api/v1/map` payload from the backend envelope.
 * @returns Validated map payload; validation does not mutate the decoded response object.
 * @throws {TypeError} When required visual data cannot safely enter the presentation layer.
 */
function validateMapApiResult(result: unknown): MapApiResult {
  if (!isRecord(result)) {
    throw new TypeError('Некорректный payload карты.');
  }

  if (!Array.isArray(result.legend)) {
    throw new TypeError('В payload карты отсутствует массив legend.');
  }

  result.legend.forEach((legendItem, index) => {
    validateLegendPayload(legendItem, index);
  });

  if (!Array.isArray(result.cameras)) {
    throw new TypeError('В payload карты отсутствует массив cameras.');
  }

  result.cameras.forEach((camera, index) => {
    validateCameraPayload(camera, index);
  });

  validateThemeRecord({
    themeRecord: result.mapStatusTheme,
    requiredGroups: MAP_STATUS_CODES,
    requiredTokenKeys: MAP_STATUS_THEME_TOKEN_KEYS,
    themeName: 'mapStatusTheme',
  });
  validateThemeRecord({
    themeRecord: result.tooltipStateTheme,
    requiredGroups: TOOLTIP_DEBT_STATES,
    requiredTokenKeys: TOOLTIP_THEME_TOKEN_KEYS,
    themeName: 'tooltipStateTheme',
  });
  validateThemeRecord({
    themeRecord: result.cameraTooltipTheme,
    requiredGroups: CAMERA_TOOLTIP_TYPES,
    requiredTokenKeys: CAMERA_TOOLTIP_THEME_TOKEN_KEYS,
    themeName: 'cameraTooltipTheme',
  });
  validateThemeRecord({
    themeRecord: result.mapStreetTheme,
    requiredGroups: MAP_STREET_THEME_GROUPS,
    requiredTokenKeys: MAP_STREET_THEME_TOKEN_KEYS,
    themeName: 'mapStreetTheme',
  });

  return result as unknown as MapApiResult;
}

/**
 * Loads the reusable map dataset for the new SVG screen.
 *
 * @returns Validated map payload with canvas, visual layers, legend and page-specific data.
 * @throws {Error} When the request or API payload contract fails.
 */
export async function loadMapData(): Promise<MapApiResult> {
  const result = await fetchApiResult('/api/v1/map', 'Не удалось загрузить карту.');

  return validateMapApiResult(result);
}

/**
 * Loads the total debt summary used by the page-level block below the map.
 *
 * @returns Backend debt summary with the display-ready total text for the page footer.
 * @throws {Error} When the debt summary request or its envelope fails.
 */
export async function loadTotalDebt(): Promise<TotalDebtApiResult> {
  const result = await fetchApiResult(
    '/api/v1/debtors/total_debt',
    'Не удалось загрузить общую сумму задолженности.',
  );

  return result as TotalDebtApiResult;
}
